import { Editor, type Monaco } from '@monaco-editor/react'
import * as monaco from 'monaco-editor'
import React, {
  type CSSProperties,
  useEffect,
  useRef,
  useState,
  forwardRef,
  useImperativeHandle,
} from 'react'

type EditorWrapperProps = {
  yamlContent?: string
  readOnly?: boolean
  onChange?: (value: string | undefined) => void // Callback for content changes
  minHeight?: number | string
  maxHeight?: number
  placeholder?: string | string[]
  id?: string
}

type MonacoEditorType = monaco.editor.IStandaloneCodeEditor

export interface EditorWrapperHandle {
  focus: () => void
}

export const EditorWrapper = forwardRef<
  EditorWrapperHandle,
  EditorWrapperProps
>(
  (
    {
      yamlContent = '',
      readOnly = false,
      minHeight = 400,
      maxHeight,
      placeholder,
      onChange,
      id,
    }: Readonly<EditorWrapperProps>,
    ref,
  ) => {
    const [content, setContent] = useState<string | undefined>(yamlContent)
    const [height, setHeight] = useState(minHeight)
    const editorRef = useRef<MonacoEditorType | null>(null)
    const monacoRef = React.useRef<Monaco | null>(null)

    // Expose focus method via ref
    useImperativeHandle(ref, () => ({
      focus: () => {
        if (editorRef.current) {
          editorRef.current.focus()
        }
      },
    }))

    // Define a custom theme
    useEffect(() => {
      monaco.editor.defineTheme('customLightYamlTheme', {
        base: 'vs',
        inherit: true,
        rules: [
          { token: 'string.yaml', foreground: '008000' },
          { token: 'key.yaml', foreground: '2b91af' },
        ],
        colors: {
          'editor.background': '#ffffff',
          'editor.foreground': '#000000',
          'editor.lineHighlightBackground': '#f4f4f4',
          'editorWidget.border': '#cccccc',
          'editorWidget.background': '#ffffff',
        },
      })
    }, [])

    useEffect(() => {
      setContent(yamlContent)
    }, [yamlContent])

    function handleEditorDidMount(editor: MonacoEditorType, monaco: Monaco) {
      editorRef.current = editor
      monacoRef.current = monaco
      updateEditorHeight()

      if (placeholder) {
        addPlaceholder(editor, monaco, placeholder)
      }
    }

    function updateEditorHeight() {
      if (editorRef.current && monacoRef.current) {
        setHeight(
          'calc(100vh - var(--nav-bar-height) - 2 * var(--standard-padding))',
        )
      }
    }

    const handleEditorChange = (value: string | undefined) => {
      if (!readOnly && onChange) {
        setContent(value)
        onChange(value)
        updateEditorHeight()
      }
    }

    function addPlaceholder(
      editor: MonacoEditorType,
      monaco: Monaco,
      placeholder: string | string[],
    ) {
      const model = editor.getModel()
      const widgetId = 'editor.placeholder'

      const placeholderText = Array.isArray(placeholder)
        ? placeholder.join('\n')
        : placeholder

      const widget: monaco.editor.IContentWidget = {
        getId: () => widgetId,
        getDomNode: () => {
          const domNode = document.createElement('div')
          const pre = document.createElement('pre')
          pre.textContent = placeholderText
          pre.style.opacity = '0.5'
          pre.style.paddingLeft = '4px'
          pre.style.fontStyle = 'italic'
          pre.style.pointerEvents = 'none'
          pre.style.whiteSpace = 'pre'
          pre.style.fontSize = '16px'
          pre.style.fontFamily = 'monospace'
          pre.style.paddingLeft = '8px'
          domNode.appendChild(pre)
          return domNode
        },
        getPosition: () => {
          const lineNumber = 1
          return {
            position: { lineNumber, column: 1 },
            preference: [monaco.editor.ContentWidgetPositionPreference.EXACT],
          }
        },
      }

      const updatePlaceholderVisibility = () => {
        const isEmpty = model?.getValue() === ''
        if (isEmpty) {
          editor.addContentWidget(widget)
        } else {
          editor.removeContentWidget(widget)
        }
      }

      // Initial check and subscription
      updatePlaceholderVisibility()
      model?.onDidChangeContent(() => {
        updatePlaceholderVisibility()
      })
    }

    const style: CSSProperties = {
      border: '1px solid #ccc',
      borderRadius: '4px',
      overflow: 'hidden',
      minHeight: `${minHeight}px`,
      height: `${height}px`,
      minWidth: '100%',
    }
    if (maxHeight) {
      style.maxHeight = maxHeight
    }

    return (
      <div style={style} id={id}>
        <Editor
          theme="customLightYamlTheme"
          language="yaml"
          options={{
            scrollbar: {
              vertical: 'hidden',
              horizontal: 'hidden',
              alwaysConsumeMouseWheel: false,
            },
            minimap: { enabled: false },
            overviewRulerLanes: 0,
            folding: true,
            scrollBeyondLastColumn: 0,
            automaticLayout: true,
            scrollBeyondLastLine: false,
            readOnly: readOnly,
            formatOnPaste: true,
            insertSpaces: true,
            wordWrap: 'on',
            fontSize: 16,
            fontFamily: 'monospace',
            tabSize: 2,
            lineNumbers: 'on',
            padding: { top: 16, bottom: 16 },
          }}
          height={height}
          value={readOnly ? yamlContent : content}
          onMount={handleEditorDidMount}
          onChange={handleEditorChange}
          loading={<div>Loading editor...</div>}
        />
      </div>
    )
  },
)
